import {CommandBase} from "./commandBase";
import {RemoteConstantsModel} from "../models/remoteConstants.model";
import {identityOf, MigratableRecord, RecordMigration} from "../utils/migrateRecord";
import {ensureSectionCollection} from "./sectionCollections";

// Syncs Current's record (matched by identity: uid for metadata, id for
// entities) to Other's version: replaces the record's fields in place
// (preserving its position in the collection), OVERWRITES its localization
// rows (the point of an update is to take Other's content), replaces its
// image pack when Other has one, and adds any pack-referenced images Current
// lacks. Shared image assets are never overwritten — only missing entries
// arrive. One history entry, so one undo reverts the whole sync.
export class UpdateRecordCommand extends CommandBase {
    migration: RecordMigration;

    constructor(migration: RecordMigration) {
        super();
        this.migration = migration;
    }

    execute(data: RemoteConstantsModel): boolean {
        const collection = ensureSectionCollection(data, this.migration.sectionKey);
        if (!collection) return false;

        const index = collection.findIndex(r => identityOf(r, this.migration.sectionKey) === this.migration.identity);
        if (index < 0) return false; // record vanished (race with classifier — refuse)

        // Deep-clone so subsequent edits in Current don't mutate Other's
        // in-memory state.
        const cloned: MigratableRecord = JSON.parse(JSON.stringify(this.migration.record));
        collection[index] = cloned;

        // Localization: overwrite — syncing the record means syncing its text.
        if (!data.localization) data.localization = {};
        for (const row of this.migration.locRows) {
            if (!data.localization[row.lang]) data.localization[row.lang] = {};
            data.localization[row.lang][row.key] = row.value;
        }

        // Image pack: replace when Other has one. When Other has none, leave
        // Current's alone — an absent pack on Other is "no opinion", not
        // "remove the images".
        if (this.migration.packRow) {
            if (!data.imagePacks) data.imagePacks = {};
            data.imagePacks[this.migration.identity] = JSON.parse(JSON.stringify(this.migration.packRow));
        }

        // Image metadata rows: only those Current didn't already have.
        if (!data.images) data.images = {};
        for (const row of this.migration.imageRows) {
            if (data.images[row.key] === undefined) {
                data.images[row.key] = JSON.parse(JSON.stringify(row.value));
            }
        }

        return true;
    }
}
